import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShieldAlert, LayoutGrid, List, Users, Coins } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { ADMIN_PANELS } from "@/components/admin/AdminTabs";

const TABS = [
  { k: "overview", label: "Overview", icon: LayoutGrid },
  { k: "moderation", label: "Moderation", icon: ShieldAlert },
  { k: "listings", label: "All Listings", icon: List },
  { k: "users", label: "Users", icon: Users },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";

  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [listings, setListings] = useState([]);
  const [users, setUsers] = useState([]);
  const [confirmId, setConfirmId] = useState(null);
  const [creditTarget, setCreditTarget] = useState(null);
  const [creditAmount, setCreditAmount] = useState("");
  const [creditNote, setCreditNote] = useState("");
  const [creditBusy, setCreditBusy] = useState(false);
  const [roleTarget, setRoleTarget] = useState(null);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/");
  }, [user, loading, navigate]);

  const loadAll = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/reports").then((r) => setReports(r.data)).catch(() => {});
    api.get("/admin/listings").then((r) => setListings(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (user?.role === "admin") loadAll(); }, [user, loadAll]);

  const resolveReport = async (id) => {
    try {
      await api.post(`/admin/reports/${id}/resolve`);
      toast.success("Report marked as resolved");
      setReports((rs) => rs.filter((r) => r.id !== id));
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    } catch { toast.error("Could not resolve report. Please try again."); }
  };

  const removeListing = async (listingId) => {
    try {
      await api.post(`/admin/listings/${listingId}/remove`);
      toast.success("Listing removed from the site");
      setReports((rs) => rs.filter((r) => r.listing_id !== listingId));
      setListings((ls) => ls.map((l) => (l.id === listingId ? { ...l, status: "removed" } : l)));
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    } catch { toast.error("Could not remove listing. Please try again."); }
  };

  const openCredits = (u) => { setCreditTarget(u); setCreditAmount(""); setCreditNote(""); };

  const submitCredits = async () => {
    const amount = Number(creditAmount);
    if (!amount || amount <= 0) { toast.error("Enter an amount greater than 0"); return; }
    setCreditBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${creditTarget.id}/credits`, { amount, note: creditNote });
      toast.success(`Added €${amount.toFixed(2)} to ${creditTarget.name}. New balance €${data.new_balance.toFixed(2)}.`);
      setUsers((us) => us.map((u) => (u.id === creditTarget.id ? { ...u, credit_balance: data.new_balance } : u)));
      setCreditTarget(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add credits"); }
    finally { setCreditBusy(false); }
  };

  const confirmRole = async () => {
    const u = roleTarget;
    const newRole = u.role === "admin" ? "user" : "admin";
    try {
      await api.post(`/admin/users/${u.id}/role`, { role: newRole });
      toast.success(newRole === "admin" ? `${u.name} is now an admin` : `Admin access removed from ${u.name}`);
      setUsers((us) => us.map((x) => (x.id === u.id ? { ...x, role: newRole } : x)));
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update role"); }
    finally { setRoleTarget(null); }
  };

  if (!user || user.role !== "admin") return <div className="min-h-screen"><Header /></div>;

  const Panel = ADMIN_PANELS[tab] || ADMIN_PANELS.overview;
  const ctx = { stats, reports, listings, users, navigate, resolveReport, removeListing: setConfirmId,
    currentUserId: user.id, onAddCredits: openCredits, onToggleRole: setRoleTarget };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full flex-1">
        <div className="flex items-center gap-2 mb-6">
          <span className="grid place-items-center w-9 h-9 rounded-lg bg-slate-900 text-white"><ShieldAlert size={18} /></span>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Admin</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <aside className="lg:col-span-1">
            <nav className="bg-white border border-slate-200 rounded-lg p-2 space-y-1 sticky top-20">
              {TABS.map((t) => (
                <button key={t.k} data-testid={`admin-tab-${t.k}`} onClick={() => setParams({ tab: t.k })}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${tab === t.k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                  <t.icon size={16} /> {t.label}
                  {t.k === "moderation" && reports.length > 0 && (
                    <span className="ml-auto text-xs bg-red-500 text-white rounded-full px-1.5 py-0.5">{reports.length}</span>
                  )}
                </button>
              ))}
            </nav>
          </aside>

          <main className="lg:col-span-3">
            <Panel ctx={ctx} />
          </main>
        </div>
      </div>
      <Footer />

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent data-testid="remove-listing-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this listing?</AlertDialogTitle>
            <AlertDialogDescription>
              It will no longer be visible to buyers on the site. You can't undo this.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remove-listing-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="remove-listing-confirm"
              onClick={() => { const id = confirmId; setConfirmId(null); removeListing(id); }}
              className="bg-red-600 hover:bg-red-700 text-white">
              Remove ad
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!creditTarget} onOpenChange={(o) => !o && setCreditTarget(null)}>
        <DialogContent data-testid="add-credits-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Coins size={18} className="text-emerald-600" /> Give free credits</DialogTitle>
            <DialogDescription>
              Add credit to <span className="font-medium text-slate-700">{creditTarget?.name}</span> ({creditTarget?.email}).
              Current balance €{(creditTarget?.credit_balance ?? 0).toFixed(2)}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="credit-amount">Amount (€)</Label>
              <Input id="credit-amount" type="number" min="0" step="0.01" value={creditAmount}
                data-testid="add-credits-amount" onChange={(e) => setCreditAmount(e.target.value)}
                placeholder="e.g. 20" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="credit-note">Note / reason (optional)</Label>
              <Input id="credit-note" value={creditNote} data-testid="add-credits-note"
                onChange={(e) => setCreditNote(e.target.value)} placeholder="e.g. Goodwill / promo" className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditTarget(null)} data-testid="add-credits-cancel">Cancel</Button>
            <Button onClick={submitCredits} disabled={creditBusy} data-testid="add-credits-confirm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {creditBusy ? "Adding…" : "Add credits"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!roleTarget} onOpenChange={(o) => !o && setRoleTarget(null)}>
        <AlertDialogContent data-testid="role-change-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {roleTarget?.role === "admin" ? "Revoke admin access?" : "Make this user an admin?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {roleTarget?.role === "admin"
                ? <>{roleTarget?.name} will lose all admin permissions and become a regular user.</>
                : <><span className="font-medium">{roleTarget?.name}</span> will get full admin access, the same permissions as every other admin, including moderation, user management and granting credits.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="role-change-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="role-change-confirm" onClick={confirmRole}
              className={roleTarget?.role === "admin" ? "bg-red-600 hover:bg-red-700 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"}>
              {roleTarget?.role === "admin" ? "Revoke admin" : "Make admin"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
